import {
  getRentOwnerGroupKey,
  isNilOrEmpty,
  MAX_INVALIDATION_ENTITY_IDS,
  RentGroupBy,
  type RentMatrixPage,
  type RentRow,
} from '@rentguru/commons-utils';
import compact from 'lodash/compact';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

const RENT_DATA_VERSION_SEPARATOR = '.';
const RENT_DATA_VERSION_PART = /^\d+$/;
const OLDER = -1;

export type PendingRentInvalidation = { kind: 'all' } | { kind: 'leases'; leaseIds: Set<string> };

export type RentLiveViewHandler = (pending: PendingRentInvalidation) => void;

type RentInvalidationPlan = { kind: 'patch'; leaseIds: string[] } | { kind: 'refetch' };

const parseRentDataVersion = (version: string | null | undefined): number[] | null => {
  if (isNilOrEmpty(version)) {
    return null;
  }
  const parts = version.split(RENT_DATA_VERSION_SEPARATOR);
  return parts.every((part) => RENT_DATA_VERSION_PART.test(part)) ? parts.map(Number) : null;
};

export const compareRentDataVersions = (
  version: string | null | undefined,
  otherVersion: string | null | undefined
): number | null => {
  const parts = parseRentDataVersion(version);
  const otherParts = parseRentDataVersion(otherVersion);
  if (isNil(parts) || isNil(otherParts) || parts.length !== otherParts.length) {
    return null;
  }
  const differingIndex = parts.findIndex((part, index) => part !== otherParts[index]);
  return differingIndex === -1 ? 0 : Math.sign(parts[differingIndex] - otherParts[differingIndex]);
};

const isOlderRentVersion = (version: string | null | undefined, reference: string | null | undefined): boolean =>
  compareRentDataVersions(version, reference) === OLDER;

export const applyRentRowPatch = (
  page: RentMatrixPage,
  rows: RentRow[],
  patchVersion: string | null | undefined,
  appliedVersions: ReadonlyMap<string, string>
): RentMatrixPage => {
  if (isOlderRentVersion(patchVersion, page.dataVersion)) {
    return page;
  }
  const rowsById = rows.reduce<Map<string, RentRow>>(
    (acc, row) => (isOlderRentVersion(patchVersion, appliedVersions.get(row.rowId)) ? acc : acc.set(row.rowId, row)),
    new Map()
  );
  if (!page.rows.some((row) => rowsById.has(row.rowId))) {
    return page;
  }
  return { ...page, rows: page.rows.map((row) => rowsById.get(row.rowId) ?? row) };
};

export const recordRentRowPatch = (
  appliedVersions: ReadonlyMap<string, string>,
  rows: RentRow[],
  patchVersion: string | null | undefined
): Map<string, string> => {
  if (isNilOrEmpty(patchVersion)) {
    return new Map(appliedVersions);
  }
  return rows.reduce<Map<string, string>>(
    (acc, { rowId }) => (isOlderRentVersion(patchVersion, acc.get(rowId)) ? acc : acc.set(rowId, patchVersion)),
    new Map(appliedVersions)
  );
};

export const hasPageOrderDrift = (pages: RentMatrixPage[]): boolean => {
  const versions = compact(pages.map((page) => page.dataVersion));
  const referenceVersion = head(versions);
  return versions.some((version) => {
    const comparison = compareRentDataVersions(version, referenceVersion);
    return !isNil(comparison) && comparison !== 0;
  });
};

const GROUP_KEY_OF: Record<RentGroupBy, (row: RentRow) => (string | undefined)[]> = {
  [RentGroupBy.BUILDING]: ({ buildingIds, buildingNames }) => [head(buildingIds), head(buildingNames)],
  [RentGroupBy.OWNER]: ({ ownerIds }) => [getRentOwnerGroupKey(ownerIds) ?? undefined],
};

export const movesRowsAcrossGroups = (
  pages: (RentMatrixPage | undefined)[],
  rows: RentRow[],
  groupBy: RentGroupBy | null
): boolean => {
  if (isNil(groupBy)) {
    return false;
  }
  const groupKeyOf = GROUP_KEY_OF[groupBy];
  const loadedRowsById = new Map(compact(pages).flatMap((page) => page.rows.map((row) => [row.rowId, row])));
  return rows.some((row) => {
    const loadedRow = loadedRowsById.get(row.rowId);
    return !isNil(loadedRow) && !isEqual(groupKeyOf(loadedRow), groupKeyOf(row));
  });
};

export const mergeRentInvalidation = (
  pending: PendingRentInvalidation | null,
  entityIds: string[] | null | undefined
): PendingRentInvalidation => {
  if (isNil(entityIds) || pending?.kind === 'all') {
    return { kind: 'all' };
  }
  return { kind: 'leases', leaseIds: new Set([...(pending?.leaseIds ?? []), ...entityIds]) };
};

export const planRentInvalidation = (
  pending: PendingRentInvalidation,
  loadedLeaseIds: ReadonlySet<string>
): RentInvalidationPlan => {
  if (pending.kind === 'all' || isEmpty(pending.leaseIds) || pending.leaseIds.size > MAX_INVALIDATION_ENTITY_IDS) {
    return { kind: 'refetch' };
  }
  const leaseIds = [...pending.leaseIds];
  if (leaseIds.some((leaseId) => !loadedLeaseIds.has(leaseId))) {
    return { kind: 'refetch' };
  }
  return { kind: 'patch', leaseIds };
};
